import { useReactFlow } from "@xyflow/react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Database, Trash2, TriangleAlert } from "lucide-mui";
import type { ReactNode } from "react";
import {
  HIVE_DATA_SOURCE,
  PROSPECT_ORDER_TRIGGER,
  SCHEDULE_TRIGGER,
  type ScheduleTriggerConfig,
  type WorkflowSubjectKind,
  type AgentContractOption,
  type AgentDefinition,
  type AgentOutputField,
  type DataSourceOption,
  type ProspectList,
  type WorkflowDataType,
} from "../../../api/client.ts";
import AdvancedModelFold from "../../../components/agent/AdvancedModelFold.tsx";
import FieldSpecInputs from "../../../components/fields/FieldSpecInputs.tsx";
import { fieldDefaults } from "../../../components/fields/fieldSpecs.ts";
import MatchStrengthSelect from "../../../components/signals/MatchStrengthSelect.tsx";
import SlackChannelPicker from "../../../components/signals/SlackChannelPicker.tsx";
import VerdictCondition from "../../../components/signals/VerdictCondition.tsx";
import DataSourceFieldInputs, { defaultsFor } from "./DataSourceFields.tsx";
import { UNCONFIGURED_TITLE } from "./DataSourceNode.tsx";
import {
  HIVE_FALLBACK,
  formatSpendCap,
  lockedOn,
  spendCapField,
  unknownOption,
  type DataSourceSelection,
} from "./dataSources.ts";
import ScheduleFields from "./ScheduleFields.tsx";
import ScheduleRuns from "./ScheduleRuns.tsx";
import { KIND_ICONS, nodeLabel } from "./StepNode.tsx";
import {
  CONTRACT_LABELS,
  DEFAULT_MAX_ORDERS_PER_DAY,
  DEFAULT_MAX_RUNS_PER_DAY,
  KIND_META,
  LIST_CONTRACTS,
  SUBJECT_LABELS,
  TRIGGER_META,
  agentInfo,
  defaultConditionFor,
  type AgentData,
  type OrderDefaults,
  type ProspectOrderStepData,
  type ConditionData,
  type ConditionEdge,
  type FieldConditionOp,
  type StepData,
  type StepNode,
} from "./types.ts";
import { DATA_TYPE_UNAVAILABLE, type Hint } from "./validation.ts";

export interface PanelOptions {
  tenantId: string;
  triggerType: string;
  // What each step runs on: the trigger's subject, or the order a step made.
  subjects: Map<string, WorkflowSubjectKind>;
  // A new workflow: the data type can still be picked.
  isNew: boolean;
  readOnly: boolean;
  dataTypes: WorkflowDataType[];
  prospectLists: ProspectList[];
  // Agents this tenant's workflows can run, and what each output contract does.
  agents: AgentDefinition[];
  contracts: AgentContractOption[];
  // The data-source catalog, with availability for this tenant's organization.
  dataSources: DataSourceOption[];
  // Opens a data-source pill's panel (its settings), as clicking the pill does.
  onOpenDataSource: (sel: DataSourceSelection) => void;
  // A saved schedule workflow: its id and whether it is on (for its runs), its
  // schedule and next slot as saved.
  savedSchedule?: {
    workflowId: string;
    active: boolean;
    schedule: ScheduleTriggerConfig;
    nextFireAt: string | null;
  };
}

interface Props extends PanelOptions {
  node: StepNode | undefined;
  edge: ConditionEdge | undefined;
  // A data-source pill, when one is selected instead of a step or edge.
  dataSource: DataSourceSelection | null;
  nodes: StepNode[];
  edges: ConditionEdge[];
  hints: Hint[];
  width: number;
}

const promptSlotProps = {
  input: { sx: { fontFamily: "monospace", fontSize: "0.85rem" } },
};

const fieldProps = (disabled: boolean) => ({ size: "small" as const, fullWidth: true, disabled });

const intOrNull = (v: string) => (v === "" ? null : Math.round(Number(v)));

// Keeps a saved value selectable once it has left the options, so the select never shows blank.
// Called as a function: Select reads its MenuItem children's `value` directly.
function savedOption(value: string, known: boolean, label: string) {
  return !value || known ? null : <MenuItem value={value}>{label}</MenuItem>;
}

function HintList({ hints }: { hints: Hint[] }) {
  if (!hints.length) return null;
  return (
    <Stack spacing={0.5}>
      {hints.map((h, i) => (
        <Alert key={i} severity={h.level} variant="outlined" sx={{ py: 0 }}>
          {h.message}
        </Alert>
      ))}
    </Stack>
  );
}

// The opted-in source's one-liner under its row: its cap, or that one is missing.
function capSummary(option: DataSourceOption, config: Record<string, unknown>) {
  const cap = spendCapField(option);
  if (!cap) return null;
  const formatted = formatSpendCap(config[cap.name]);
  return formatted ? { text: `≤ ${formatted}/run`, error: false } : { text: "No cap set", error: true };
}

// Which sources the agent uses: opt-in rows for the ones its definition allows,
// Vayken always on. A source's own settings live in its pill's panel, which
// opting in opens and each row's "Settings" link reopens.
function DataSourceSection({
  d,
  allowed,
  hints,
  opts,
  onChange,
  onOpen,
}: {
  d: AgentData;
  allowed: string[];
  // The validation hints aimed at this agent's sources, marked on their rows.
  hints: Hint[];
  opts: PanelOptions;
  onChange: (next: AgentData["data_sources"]) => void;
  onOpen: (key: string) => void;
}) {
  const options = opts.dataSources.length ? opts.dataSources : [HIVE_FALLBACK];
  // Opted-in sources stay listed even when the definition no longer allows
  // them, and so do keys the catalog doesn't know: both so they can be removed.
  const rows = [
    ...options.filter((o) => o.implicit || allowed.includes(o.key) || o.key in d.data_sources),
    ...Object.keys(d.data_sources)
      .filter((k) => !options.some((o) => o.key === k))
      .map(unknownOption),
  ];
  const toggle = (option: DataSourceOption, on: boolean) => {
    const next = { ...d.data_sources };
    if (on) next[option.key] = defaultsFor(option);
    else delete next[option.key];
    onChange(next);
    if (on) onOpen(option.key);
  };

  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">Data sources</Typography>
      {rows.map((o) => {
        const locked = lockedOn(o, allowed);
        const opted = locked || o.key in d.data_sources;
        const unconfigured = !o.implicit && !o.configured;
        const rowHints = hints.filter((h) => h.dataSourceKey === o.key);
        const summary = opted && !locked ? capSummary(o, d.data_sources[o.key] ?? {}) : null;
        return (
          <Box key={o.key}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={opted}
                  disabled={opts.readOnly || locked}
                  onChange={(e) => toggle(o, e.target.checked)}
                />
              }
              label={
                <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
                  <Typography variant="body2">
                    {locked
                      ? `${o.label} · always on${o.key === HIVE_DATA_SOURCE ? ` · ${d.hive_tool_count} tools` : ""}`
                      : o.label}
                  </Typography>
                  {o.spends_money && <Chip size="small" label="Spends money" variant="outlined" />}
                  {o.always_on && <Chip size="small" label="Free" variant="outlined" />}
                  {unconfigured && (
                    <Tooltip title={o.unavailable_reason || UNCONFIGURED_TITLE}>
                      <Chip size="small" label="Not configured" color="warning" />
                    </Tooltip>
                  )}
                </Stack>
              }
            />
            {opted && !locked && (
              <Stack direction="row" spacing={1} sx={{ pl: 4, alignItems: "center" }}>
                {rowHints.length > 0 && (
                  <Tooltip title={rowHints.map((h) => h.message).join(" ")}>
                    <Box
                      sx={{
                        display: "flex",
                        color: rowHints.some((h) => h.level === "error") ? "error.main" : "warning.main",
                      }}
                    >
                      <TriangleAlert fontSize="small" />
                    </Box>
                  </Tooltip>
                )}
                {summary && (
                  <Typography
                    variant="caption"
                    sx={{ color: summary.error ? "error.main" : "text.secondary" }}
                  >
                    {summary.text}
                  </Typography>
                )}
                <Link component="button" type="button" variant="caption" onClick={() => onOpen(o.key)}>
                  Settings
                </Link>
              </Stack>
            )}
          </Box>
        );
      })}
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Vayken is always available. Click a data source to set its limits.
      </Typography>
    </Stack>
  );
}

function ProspectListSelect({
  listId,
  listName,
  lists,
  onChange,
  helperText,
  readOnly,
}: {
  listId: string;
  listName: string;
  lists: ProspectList[];
  onChange: (listId: string, listName: string) => void;
  helperText: string;
  readOnly: boolean;
}) {
  const known = lists.some((l) => l.id === listId);
  return (
    <TextField
      label="Collection"
      select
      {...fieldProps(readOnly)}
      value={listId}
      onChange={(e) =>
        onChange(e.target.value, lists.find((l) => l.id === e.target.value)?.name ?? "")
      }
      helperText={lists.length === 0 ? "No collections for this tenant yet." : helperText}
      error={!listId}
    >
      {lists
        .filter((l) => !l.archived || l.id === listId)
        .map((l) => (
          <MenuItem key={l.id} value={l.id}>
            {l.name}
          </MenuItem>
        ))}
      {savedOption(listId, known, listName || "Unknown collection")}
    </TextField>
  );
}

// What an "Add to collection" step copies, by the agent it is chained under:
// each of those agents keeps its finds on a holding list (the order's, or the
// signal workflow's own).
function copyHelperText(parent: StepData | undefined, subject: WorkflowSubjectKind): string {
  const contract = parent?.kind === "agent" ? parent.contract : undefined;
  switch (contract) {
    case "prospect_list":
      return "Every company the agent above found is copied here, reasons and labels included.";
    case "signal_brief":
      return "The buyer the research agent above identified is copied here, with its headline and verdict.";
    case "contact_list":
      return "Every person the contact research agent above found is copied here, with their company.";
    default:
      return subject === PROSPECT_ORDER_TRIGGER
        ? "Chain this under the prospecting or contact research agent to copy what it found."
        : "The signal's company is added here.";
  }
}

function AgentFields({
  node,
  d,
  parent,
  subject,
  sourceHints,
  opts,
  enabledSwitch,
}: {
  node: StepNode;
  d: AgentData;
  // What this step is chained under; some agents only fit under another agent.
  parent: StepData | undefined;
  // What the agent runs on: the signal, or a prospect order.
  subject: WorkflowSubjectKind;
  sourceHints: Hint[];
  opts: PanelOptions;
  enabledSwitch: ReactNode;
}) {
  const { updateNodeData, setEdges } = useReactFlow<StepNode, ConditionEdge>();
  const { readOnly } = opts;
  const agent = opts.agents.find((a) => a.id === d.agent_id);
  const parentContract = parent?.kind === "agent" ? parent.contract : undefined;
  // Agents whose contract works on this subject and fits the parent (contact
  // research only under the prospecting or signal research agent); the
  // current one stays listed so a saved step never shows blank.
  const choices = opts.agents.filter(
    (a) =>
      a.id === d.agent_id ||
      (a.enabled &&
        (a.output_contract !== "contact_list" ||
          parentContract === "prospect_list" ||
          parentContract === "signal_brief") &&
        opts.contracts
          .find((c) => c.key === a.output_contract)
          ?.subject_kinds.includes(subject)),
  );

  const pick = (agentId: string) => {
    const info = agentInfo(
      opts.agents.find((a) => a.id === agentId),
      opts.contracts,
    );
    // Keep only the sources the new definition allows.
    const data_sources = Object.fromEntries(
      Object.entries(d.data_sources).filter(([k]) => info.allowed_data_sources.includes(k)),
    );
    const next: AgentData = { ...d, agent_id: agentId, ...info, data_sources };
    updateNodeData(node.id, next);
    // What a branch can say depends on the agent's contract: start them over.
    setEdges((es) =>
      es.map((e) =>
        e.source === node.id
          ? {
              ...e,
              data: { ...defaultConditionFor(next), requires_approval: e.data?.requires_approval },
            }
          : e,
      ),
    );
  };

  return (
    <>
      <TextField
        label="Agent"
        select
        {...fieldProps(readOnly)}
        value={d.agent_id}
        onChange={(e) => pick(e.target.value)}
        error={!d.agent_id}
        helperText={
          choices.length === 0
            ? "No agent can run here yet. Developers define agents under Settings → Agents."
            : "Developers define agents under Settings → Agents. Some agents only run under another agent."
        }
      >
        {choices.map((a) => (
          <MenuItem key={a.id} value={a.id}>
            {a.name}
            {a.tenant_id ? " (tenant copy)" : ""}
            {!a.enabled ? " (disabled)" : ""}
          </MenuItem>
        ))}
        {savedOption(d.agent_id, !!agent, d.agent_name || "Unknown agent")}
      </TextField>
      {agent && (
        <Stack spacing={1}>
          {agent.description && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {agent.description}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <Chip size="small" label={CONTRACT_LABELS[agent.output_contract]} color="info" variant="outlined" />
            <Chip size="small" label={`${agent.hive_tools.length} tools`} variant="outlined" />
            {agent.web_access && <Chip size="small" label="Web" variant="outlined" />}
            <Chip size="small" label={`≤ ${agent.max_turns} turns`} variant="outlined" />
          </Box>
          {agent.output_contract === "structured" && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Result fields: {agent.output_fields.map((f) => f.name).join(", ")}
            </Typography>
          )}
        </Stack>
      )}
      {d.agent_id && (
        <DataSourceSection
          d={d}
          allowed={agent?.data_sources ?? d.allowed_data_sources}
          hints={sourceHints}
          opts={opts}
          onChange={(data_sources) => updateNodeData(node.id, { data_sources })}
          onOpen={(key) => opts.onOpenDataSource({ agentNodeId: node.id, key })}
        />
      )}
      <TextField
        label={d.requires_instructions ? "Instructions" : "Extra guidance"}
        placeholder={
          d.contract === "signal_brief"
            ? "e.g. Find out which companies the incident involves, look for follow-up " +
              "coverage and official reports, and judge whether it is a real, recent event " +
              "our ICP would care about."
            : "Optional. Anything the agent should know for this workflow, on top of its own instructions."
        }
        helperText={
          d.contract === "contact_list" && subject === "signal"
            ? "The signal and its research brief (buyer, roles to approach) are always included."
            : LIST_CONTRACTS.has(d.contract)
              ? "The order's own brief (ICP, filters, requested count) is always included."
              : "Added to the agent's own instructions for this workflow only."
        }
        multiline
        minRows={4}
        maxRows={16}
        {...fieldProps(readOnly)}
        value={d.instructions}
        onChange={(e) => updateNodeData(node.id, { instructions: e.target.value })}
        error={d.requires_instructions && !d.instructions.trim()}
        slotProps={promptSlotProps}
      />
      {/* Signals arrive by themselves, and so do the orders a signal workflow makes. */}
      {opts.triggerType === "signal" && (
        <TextField
          label="Max runs per day"
          type="number"
          {...fieldProps(readOnly)}
          value={d.max_runs_per_day ?? ""}
          placeholder={String(DEFAULT_MAX_RUNS_PER_DAY)}
          onChange={(e) => updateNodeData(node.id, { max_runs_per_day: intOrNull(e.target.value) })}
          slotProps={{ htmlInput: { min: 1, max: 1000 } }}
          helperText={`Spend cap (default ${DEFAULT_MAX_RUNS_PER_DAY}); runs bill the org's Claude plan.`}
        />
      )}
      {(opts.triggerType === PROSPECT_ORDER_TRIGGER || opts.triggerType === SCHEDULE_TRIGGER) && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Runs bill the tenant's organization's Claude plan.
        </Typography>
      )}
      {enabledSwitch}
      {d.agent_id && (
        <AdvancedModelFold
          // One fold per step: switching steps folds it again.
          key={node.id}
          tenantId={opts.tenantId}
          value={d.model}
          onChange={(model) => updateNodeData(node.id, { model })}
          inherited={agent?.model}
          defaultLabel={agent?.model ? "Agent's model" : "Default"}
          disabled={readOnly}
        />
      )}
    </>
  );
}

// "Create prospect order": what the drafter is told, the defaults the order
// gets where the record says nothing, and what a later change to the page does.
function CreateOrderFields({
  d,
  opts,
  patch,
  enabledSwitch,
}: {
  d: ProspectOrderStepData;
  opts: PanelOptions;
  patch: (p: Partial<ProspectOrderStepData>) => void;
  enabledSwitch: ReactNode;
}) {
  const { readOnly } = opts;
  const setDefault = (p: Partial<OrderDefaults>) => patch({ defaults: { ...d.defaults, ...p } });

  return (
    <>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        The local model drafts the order (title, offering, ICP, buyer persona, count, deadline)
        from the record; what it leaves blank comes from the tenant's company profile. The order
        belongs to the tenant's organization and shows on its Orders page; the steps below run
        on it.
      </Typography>
      <TextField
        label="Drafting guidance"
        placeholder="e.g. Each page in this database is one sales request. The 'Market' property is the country."
        helperText="Optional. How records here phrase a request for prospects."
        multiline
        minRows={3}
        maxRows={12}
        {...fieldProps(readOnly)}
        value={d.instructions}
        onChange={(e) => patch({ instructions: e.target.value })}
        slotProps={promptSlotProps}
      />
      <TextField
        label="When the page changes again"
        select
        {...fieldProps(readOnly)}
        value={d.on_repeat}
        onChange={(e) => patch({ on_repeat: e.target.value as ProspectOrderStepData["on_repeat"] })}
        helperText={
          d.on_repeat === "update"
            ? "Redraft the order it made before and run the steps again, unless someone is already working it."
            : "Every new signal makes another order."
        }
      >
        <MenuItem value="update">Update its open order</MenuItem>
        <MenuItem value="new">Create a new order</MenuItem>
      </TextField>
      <Divider textAlign="left">
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Defaults, where the record says nothing
        </Typography>
      </Divider>
      <TextField
        label="Companies wanted"
        type="number"
        {...fieldProps(readOnly)}
        value={d.defaults.requested_count ?? ""}
        onChange={(e) => setDefault({ requested_count: intOrNull(e.target.value) })}
        slotProps={{ htmlInput: { min: 1 } }}
      />
      <TextField
        label="Max new orders per day"
        type="number"
        {...fieldProps(readOnly)}
        value={d.max_orders_per_day ?? ""}
        placeholder={String(DEFAULT_MAX_ORDERS_PER_DAY)}
        onChange={(e) => patch({ max_orders_per_day: intOrNull(e.target.value) })}
        slotProps={{ htmlInput: { min: 1, max: 200 } }}
        helperText={`Default ${DEFAULT_MAX_ORDERS_PER_DAY}. Redrafts don't count.`}
      />
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Tip: require approval on the edge to the prospecting agent, so someone reviews the drafted
        order before the agent spends.
      </Typography>
      {enabledSwitch}
    </>
  );
}

function NodeEditor({
  node,
  parent,
  hints,
  opts,
}: {
  node: StepNode;
  parent: StepData | undefined;
  hints: Hint[];
  opts: PanelOptions;
}) {
  const { updateNodeData, deleteElements } = useReactFlow<StepNode, ConditionEdge>();
  const patch = (p: Partial<StepData>) => updateNodeData(node.id, p);
  const { readOnly } = opts;
  const d = node.data;
  const meta = KIND_META[d.kind];
  const description = d.kind === "trigger" ? TRIGGER_META[d.trigger_type].description : meta.description;
  const subject = opts.subjects.get(node.id) ?? (opts.triggerType as WorkflowSubjectKind);
  // A source's hints show in its own panel; here they only mark its row. An
  // unavailable data type shows under the data type select instead.
  const ownHints = hints.filter((h) => !h.dataSourceKey && h.key !== DATA_TYPE_UNAVAILABLE);
  const dataType =
    d.kind === "trigger" && d.trigger_type === "signal"
      ? opts.dataTypes.find((t) => t.slug === d.data_type)
      : undefined;

  const enabledSwitch = (label: string) =>
    d.kind !== "trigger" && (
      <FormControlLabel
        control={
          <Switch
            checked={d.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
            disabled={readOnly}
          />
        }
        label={label}
      />
    );

  let fields: ReactNode;
  switch (d.kind) {
    case "trigger":
      fields = d.trigger_type === "schedule" ? (
        <ScheduleFields
          schedule={d.schedule}
          onChange={(schedule) => patch({ schedule })}
          disabled={readOnly}
          saved={opts.savedSchedule}
        />
      ) : d.trigger_type === "prospect_order" ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Nothing to configure. Every prospect order placed for this tenant runs the steps below;
          the order page shows what each did and can run them again. Pause the workflow to take
          orders by hand.
        </Typography>
      ) : (
        <>
          <TextField
            label="Data type"
            select
            {...fieldProps(readOnly || !opts.isNew)}
            value={d.data_type}
            onChange={(e) => {
              const picked = opts.dataTypes.find((t) => t.slug === e.target.value);
              patch({
                data_type: e.target.value,
                data_type_label: picked?.label ?? e.target.value,
                match_mode: "llm",
                // Each data type has its own settings; start from its defaults.
                source: fieldDefaults(picked?.config_fields ?? []),
              });
            }}
            helperText={
              opts.isNew ? "Which incoming data stream this workflow evaluates." : "Fixed after creation"
            }
          >
            {opts.dataTypes.map((t) => (
              <MenuItem key={t.slug} value={t.slug}>
                {t.label}
              </MenuItem>
            ))}
            {savedOption(
              d.data_type,
              opts.dataTypes.some((t) => t.slug === d.data_type),
              d.data_type_label || d.data_type,
            )}
          </TextField>
          {dataType?.unavailable_reason && (
            <Alert severity="warning" variant="outlined">
              {dataType.unavailable_reason} Saving is fine; the workflow sees no records until then.
            </Alert>
          )}
          {dataType && dataType.config_fields.length > 0 && (
            <FieldSpecInputs
              fields={dataType.config_fields}
              fieldOptions={dataType.field_options}
              values={d.source}
              onChange={(source) => patch({ source })}
              disabled={readOnly}
            />
          )}
          {dataType?.supports_rules && (
            <FormControlLabel
              control={
                <Switch
                  checked={d.match_mode === "rules"}
                  onChange={(e) => patch({ match_mode: e.target.checked ? "rules" : "llm" })}
                  disabled={readOnly}
                />
              }
              label="Match by rules (no LLM)"
            />
          )}
          {d.match_mode === "rules" ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Every record that passes the settings above is a match, rated by what happened. No
              LLM reads it, so the tenant's ICP is not checked.
            </Typography>
          ) : (
            <TextField
              label="What to look for"
              placeholder="Describe the event the LLM should catch in each record…"
              helperText="Every new record of this data type is evaluated against this."
              multiline
              minRows={6}
              maxRows={20}
              {...fieldProps(readOnly)}
              value={d.prompt}
              onChange={(e) => patch({ prompt: e.target.value })}
              slotProps={promptSlotProps}
            />
          )}
        </>
      );
      break;
    case "agent":
      fields = (
        <AgentFields
          node={node}
          d={d}
          parent={parent}
          subject={subject}
          sourceHints={hints.filter((h) => h.dataSourceKey)}
          opts={opts}
          enabledSwitch={enabledSwitch(subject === "signal" ? "Run automatically" : "Enabled")}
        />
      );
      break;
    case "add_to_prospect_list":
      fields = (
        <>
          <ProspectListSelect
            listId={d.collection_id}
            listName={d.collection_name}
            lists={opts.prospectLists}
            onChange={(collection_id, collection_name) => patch({ collection_id, collection_name })}
            helperText={copyHelperText(parent, subject)}
            readOnly={readOnly}
          />
          {enabledSwitch("Enabled")}
        </>
      );
      break;
    case "create_prospect_order":
      fields = (
        <CreateOrderFields d={d} opts={opts} patch={patch} enabledSwitch={enabledSwitch("Enabled")} />
      );
      break;
    case "slack_message":
      fields = (
        <>
          <SlackChannelPicker
            tenantId={opts.tenantId}
            channelId={d.channel_id}
            channelName={d.channel_name}
            onChange={(channel) =>
              patch({ channel_id: channel?.id ?? "", channel_name: channel?.name ?? "" })
            }
            disabled={readOnly}
          />
          {enabledSwitch("Enabled")}
        </>
      );
      break;
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: meta.accent }}>
        {KIND_ICONS[d.kind]}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary", flexGrow: 1 }}>
          {nodeLabel(d)}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {description}
      </Typography>
      <HintList hints={ownHints} />
      {d.kind !== "trigger" && subject !== opts.triggerType && (
        <Alert severity="info" variant="outlined">
          Runs on the {SUBJECT_LABELS[subject]} the step above created, not on the signal.
        </Alert>
      )}
      {fields}
      {d.kind !== "trigger" && !readOnly && (
        <>
          <Divider />
          <Button
            color="error"
            variant="outlined"
            size="small"
            startIcon={<Trash2 fontSize="small" />}
            onClick={() => deleteElements({ nodes: [{ id: node.id }] })}
          >
            Remove step
          </Button>
        </>
      )}
    </Stack>
  );
}

// The panel for a selected data-source pill: the same per-step settings as
// the agent step's section, on their own.
function DataSourceEditor({
  sel,
  nodes,
  hints,
  opts,
}: {
  sel: DataSourceSelection;
  nodes: StepNode[];
  hints: Hint[];
  opts: PanelOptions;
}) {
  const { updateNodeData, setNodes } = useReactFlow<StepNode, ConditionEdge>();
  const agentNode = nodes.find((n) => n.id === sel.agentNodeId);
  const d = agentNode?.data;
  if (!agentNode || d?.kind !== "agent") return null;
  const catalog = opts.dataSources.length ? opts.dataSources : [HIVE_FALLBACK];
  const option = catalog.find((o) => o.key === sel.key) ?? unknownOption(sel.key);
  const config = d.data_sources[sel.key] ?? {};
  const locked = lockedOn(option, d.allowed_data_sources);
  const unconfigured = !option.implicit && !option.configured;
  const { readOnly } = opts;

  const openAgent = () =>
    setNodes((ns) => ns.map((n) => ({ ...n, selected: n.id === agentNode.id })));
  const remove = () => {
    const next = { ...d.data_sources };
    delete next[sel.key];
    updateNodeData(agentNode.id, { data_sources: next });
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
        <Database fontSize="small" />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="overline" sx={{ display: "block", lineHeight: 1.2 }}>
            Data source
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
            {option.label}
          </Typography>
        </Box>
        {option.spends_money && <Chip size="small" label="Spends money" variant="outlined" />}
        {option.always_on && <Chip size="small" label="Free" variant="outlined" />}
      </Box>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {option.description}
      </Typography>
      <HintList hints={hints} />
      {unconfigured && (
        <Alert severity="warning" variant="outlined">
          {option.unavailable_reason || UNCONFIGURED_TITLE}.{" "}
          {locked
            ? "Runs go without it until it is set up."
            : "Saving is fine; runs will fail the source until it is set up."}
        </Alert>
      )}
      {option.implicit ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Always available; the agent's definition gives it {d.hive_tool_count} Vayken tools.
        </Typography>
      ) : locked ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Always on for this agent; there is nothing to set on the step.
        </Typography>
      ) : (
        <DataSourceFieldInputs
          option={option}
          config={config}
          onChange={(next) => updateNodeData(agentNode.id, { data_sources: { ...d.data_sources, [sel.key]: next } })}
          disabled={readOnly}
        />
      )}
      <Link component="button" type="button" variant="body2" onClick={openAgent} sx={{ alignSelf: "flex-start" }}>
        Open agent step
      </Link>
      {!locked && !readOnly && (
        <>
          <Divider />
          <Button
            color="error"
            variant="outlined"
            size="small"
            startIcon={<Trash2 fontSize="small" />}
            onClick={remove}
          >
            Remove data source
          </Button>
        </>
      )}
    </Stack>
  );
}

const OPS: { value: FieldConditionOp; label: string; ordered?: boolean }[] = [
  { value: "eq", label: "equals" },
  { value: "ne", label: "does not equal" },
  { value: "gte", label: "at least", ordered: true },
  { value: "gt", label: "more than", ordered: true },
  { value: "lte", label: "at most", ordered: true },
  { value: "lt", label: "less than", ordered: true },
  { value: "in", label: "is one of" },
];

function parseValue(field: AgentOutputField, raw: string): unknown {
  if (field.type === "integer" || field.type === "number") {
    if (raw.trim() === "") return null;
    const n = Number(raw);
    return field.type === "integer" ? Math.round(n) : n;
  }
  if (field.type === "boolean") return raw === "true";
  return raw;
}

function FieldCondition({
  fields,
  condition,
  onChange,
  disabled,
}: {
  fields: AgentOutputField[];
  condition: ConditionData;
  onChange: (next: Partial<ConditionData>) => void;
  disabled: boolean;
}) {
  const field = fields.find((f) => f.name === condition.field);
  const numeric = field?.type === "number" || field?.type === "integer";
  const op = condition.op ?? "eq";
  const ops = OPS.filter((o) => !o.ordered || numeric).filter(
    (o) => o.value !== "in" || field?.type === "enum" || field?.type === "string",
  );
  const value = condition.value;

  let valueInput: ReactNode = null;
  if (field && op === "in" && field.type === "enum") {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    valueInput = (
      <TextField
        label="Values"
        select
        {...fieldProps(disabled)}
        value={selected}
        onChange={(e) => onChange({ value: e.target.value as unknown as string[] })}
        slotProps={{ select: { multiple: true } }}
      >
        {field.enum_values.map((v) => (
          <MenuItem key={v} value={v}>
            {v}
          </MenuItem>
        ))}
      </TextField>
    );
  } else if (field && op === "in") {
    valueInput = (
      <TextField
        label="Values"
        {...fieldProps(disabled)}
        value={Array.isArray(value) ? (value as string[]).join(", ") : ""}
        onChange={(e) =>
          onChange({
            value: e.target.value
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean),
          })
        }
        helperText="Comma-separated."
      />
    );
  } else if (field && (field.type === "enum" || field.type === "boolean")) {
    const options = field.type === "enum" ? field.enum_values : ["true", "false"];
    valueInput = (
      <TextField
        label="Value"
        select
        {...fieldProps(disabled)}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange({ value: parseValue(field, e.target.value) })}
      >
        {options.map((v) => (
          <MenuItem key={v} value={v}>
            {v}
          </MenuItem>
        ))}
      </TextField>
    );
  } else if (field) {
    valueInput = (
      <TextField
        label="Value"
        {...fieldProps(disabled)}
        type={numeric ? "number" : "text"}
        value={value === undefined || value === null ? "" : String(value)}
        onChange={(e) => onChange({ value: parseValue(field, e.target.value) })}
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Result field"
        select
        {...fieldProps(disabled)}
        value={condition.field ?? ""}
        onChange={(e) =>
          onChange(
            e.target.value === ""
              ? { field: null, op: undefined, value: undefined }
              : { field: e.target.value, op: "eq", value: undefined },
          )
        }
        helperText="Leave empty to run whenever the agent records a result."
      >
        <MenuItem value="">
          <em>Any result</em>
        </MenuItem>
        {fields.map((f) => (
          <MenuItem key={f.name} value={f.name}>
            {f.name} ({f.type})
          </MenuItem>
        ))}
      </TextField>
      {field && (
        <TextField
          label="Condition"
          select
          {...fieldProps(disabled)}
          value={op}
          onChange={(e) => {
            const nextOp = e.target.value as FieldConditionOp;
            onChange({ op: nextOp, value: nextOp === "in" ? [] : undefined });
          }}
        >
          {ops.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      )}
      {valueInput}
    </Stack>
  );
}

function EdgeEditor({
  edge,
  source,
  hints,
  readOnly,
  approvable,
}: {
  edge: ConditionEdge;
  source: StepNode | undefined;
  hints: Hint[];
  readOnly: boolean;
  approvable: boolean;
}) {
  const { updateEdgeData, deleteElements } = useReactFlow<StepNode, ConditionEdge>();
  const sourceData = source?.data;
  const sourceKind = sourceData?.kind;
  const contract = sourceData?.kind === "agent" ? sourceData.contract : undefined;
  const condition: ConditionData = edge.data ?? {};
  const patch = (p: Partial<ConditionData>) => updateEdgeData(edge.id, p);

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        When does the next step run?
      </Typography>
      <HintList hints={hints} />
      {sourceData?.kind === "trigger" && sourceData.trigger_type === "signal" && (
        <MatchStrengthSelect
          value={condition.min_rating ?? null}
          onChange={(min_rating) => patch({ min_rating })}
          helperText="Only run for signals the matcher scored at least this strong. Not the research agent's confidence: that is set on the step after a research agent."
          disabled={readOnly}
        />
      )}
      {sourceData?.kind === "trigger" && sourceData.trigger_type === "prospect_order" && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Runs for every prospect order placed for this tenant.
        </Typography>
      )}
      {sourceData?.kind === "trigger" && sourceData.trigger_type === "schedule" && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Runs at every slot of the schedule.
        </Typography>
      )}
      {contract !== undefined && LIST_CONTRACTS.has(contract) && (
        <TextField
          label={contract === "contact_list" ? "Minimum contacts found" : "Minimum prospects found"}
          type="number"
          {...fieldProps(readOnly)}
          value={condition.min_results ?? ""}
          onChange={(e) => patch({ min_results: intOrNull(e.target.value) })}
          slotProps={{ htmlInput: { min: 0 } }}
          helperText="Leave empty to run whenever the agent finishes, even with no results."
        />
      )}
      {contract === "signal_brief" && (
        <VerdictCondition
          verdicts={condition.verdicts ?? []}
          minConfidence={condition.min_confidence ?? null}
          onChange={(next) => patch(next)}
          disabled={readOnly}
        />
      )}
      {contract === "structured" && sourceData?.kind === "agent" && (
        <FieldCondition
          fields={sourceData.output_fields}
          condition={condition}
          onChange={patch}
          disabled={readOnly}
        />
      )}
      {(contract === "text" || contract === "") && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Runs whenever the agent finishes; its summary is passed on.
        </Typography>
      )}
      {sourceKind === "create_prospect_order" && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Runs on the order once it has been drafted.
        </Typography>
      )}
      {sourceKind === "add_to_prospect_list" && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Runs once the companies have been added to the collection.
        </Typography>
      )}
      {approvable && <Divider />}
      {approvable && (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={!!condition.requires_approval}
              onChange={(e) => patch({ requires_approval: e.target.checked })}
              disabled={readOnly}
            />
          }
          label="Require human approval"
        />
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          The next step waits until someone approves it on the signal or order instead of
          running by itself. Use it before steps that spend: agent runs, paid data sources.
        </Typography>
      </Box>
      )}
      {!readOnly && (
        <>
          <Divider />
          <Button
            color="error"
            variant="outlined"
            size="small"
            startIcon={<Trash2 fontSize="small" />}
            onClick={() => deleteElements({ edges: [{ id: edge.id }] })}
          >
            Disconnect
          </Button>
        </>
      )}
    </Stack>
  );
}

export default function PropertiesPanel({
  node,
  edge,
  dataSource,
  nodes,
  edges,
  hints,
  width,
  ...opts
}: Props) {
  let body: ReactNode;
  if (dataSource) {
    body = (
      <DataSourceEditor
        key={`${dataSource.agentNodeId}:${dataSource.key}`}
        sel={dataSource}
        nodes={nodes}
        hints={hints.filter(
          (h) => h.nodeId === dataSource.agentNodeId && h.dataSourceKey === dataSource.key,
        )}
        opts={opts}
      />
    );
  } else if (node) {
    const parentId = edges.find((e) => e.target === node.id)?.source;
    body = (
      <NodeEditor
        key={node.id}
        node={node}
        parent={parentId ? nodes.find((n) => n.id === parentId)?.data : undefined}
        hints={hints.filter((h) => h.nodeId === node.id)}
        opts={opts}
      />
    );
  } else if (edge) {
    const source = nodes.find((n) => n.id === edge.source);
    body = (
      <EdgeEditor
        key={edge.id}
        edge={edge}
        source={source}
        hints={hints.filter((h) => h.edgeId === edge.id)}
        readOnly={opts.readOnly}
        // Nothing shows a scheduled run's steps for approval.
        approvable={opts.triggerType !== SCHEDULE_TRIGGER}
      />
    );
  } else {
    body = (
      <Stack spacing={1}>
        {opts.savedSchedule && (
          <>
            <ScheduleRuns
              workflowId={opts.savedSchedule.workflowId}
              active={opts.savedSchedule.active}
              readOnly={opts.readOnly}
            />
            <Divider sx={{ my: 1 }} />
          </>
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Nothing selected
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {opts.readOnly
            ? "Click a step or a connection label to see its settings."
            : "Click a step to edit it, or a connection label to change when the next step " +
              "runs. Drag from a step's bottom handle to another step's top handle to chain them. " +
              "The dashed pills beside an agent are its data sources; click one to set its limits."}
        </Typography>
      </Stack>
    );
  }

  return (
    <Paper variant="outlined" sx={{ width, flexShrink: 0, p: 2, overflow: "auto" }}>
      {body}
    </Paper>
  );
}
